import type { SharedLinkPrivacySettings } from "../schemas/content";
import type { ResultModuleKey, ResultsRenderVariant } from "../types";

export type ResultModuleOwnership = "shell_owned" | "policy_controlled";
export type ResultModuleVisibility = "always" | "optional";
export type ResultModuleSurface = "results" | "shared_link" | "share_artifact";

export interface ResultsModuleDefinition {
	key: ResultModuleKey;
	description: string;
	ownership: ResultModuleOwnership;
	visibility: ResultModuleVisibility;
	sduiEligible: boolean;
	allowedSurfaces: ResultModuleSurface[];
}

export const RESULTS_MODULE_REGISTRY: Record<
	ResultModuleKey,
	ResultsModuleDefinition
> = {
	score: {
		key: "score",
		description: "Core score / category framing at the top of results.",
		ownership: "shell_owned",
		visibility: "always",
		sduiEligible: false,
		allowedSurfaces: ["results", "shared_link", "share_artifact"],
	},
	likely_impact: {
		key: "likely_impact",
		description: "Plain-English likely impact framing paired with the score.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results", "shared_link", "share_artifact"],
	},
	summary: {
		key: "summary",
		description: "Primary summary / verdict block for the analysis.",
		ownership: "shell_owned",
		visibility: "always",
		sduiEligible: false,
		allowedSurfaces: ["results", "shared_link", "share_artifact"],
	},
	primary_rewrite: {
		key: "primary_rewrite",
		description: "Best rewrite candidate surfaced early in the flow.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results", "shared_link"],
	},
	narrator: {
		key: "narrator",
		description: "Narrator-style explanation / tone commentary.",
		ownership: "shell_owned",
		visibility: "always",
		sduiEligible: false,
		allowedSurfaces: ["results", "shared_link"],
	},
	patterns: {
		key: "patterns",
		description: "Detected conversational patterns or red flags.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results", "shared_link"],
	},
	transformations: {
		key: "transformations",
		description: "Expanded rewrite / transformation breakdowns.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results"],
	},
	feedback: {
		key: "feedback",
		description: "Feedback or response collection module.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results", "shared_link"],
	},
	share: {
		key: "share",
		description: "Built-in share controls for private results surfaces.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results"],
	},
	upgrade_teaser: {
		key: "upgrade_teaser",
		description: "Upgrade/teaser block for preview-tier result states.",
		ownership: "policy_controlled",
		visibility: "optional",
		sduiEligible: true,
		allowedSurfaces: ["results"],
	},
};

export const BASE_RESULTS_MODULE_ORDER: Record<
	ResultsRenderVariant,
	ResultModuleKey[]
> = {
	preview: [
		"score",
		"likely_impact",
		"summary",
		"primary_rewrite",
		"narrator",
		"patterns",
		"feedback",
		"share",
		"upgrade_teaser",
	],
	full: [
		"score",
		"likely_impact",
		"summary",
		"primary_rewrite",
		"narrator",
		"patterns",
		"transformations",
		"feedback",
		"share",
	],
	generative: [
		"score",
		"likely_impact",
		"summary",
		"primary_rewrite",
		"narrator",
		"patterns",
		"transformations",
		"feedback",
		"share",
	],
};

export const SHARE_ARTIFACT_MODULE_ORDER: ResultModuleKey[] = [
	"score",
	"likely_impact",
	"summary",
];

export const SHARED_LINK_PRIVACY_MODULE_FILTERS: Record<
	keyof SharedLinkPrivacySettings,
	ResultModuleKey[]
> = {
	hide_message: [],
	hide_score: ["score", "likely_impact"],
	hide_summary: ["summary"],
	hide_narrator: ["narrator"],
	hide_snippets: ["patterns"],
	hide_rewrites: ["primary_rewrite", "transformations"],
};

export function getBaseResultsModuleOrder(
	renderVariant: ResultsRenderVariant,
): ResultModuleKey[] {
	return [...BASE_RESULTS_MODULE_ORDER[renderVariant]];
}

export function getAllowedModulesForSurface(
	surface: ResultModuleSurface,
	renderVariant?: ResultsRenderVariant,
): ResultModuleKey[] {
	const sourceModules = renderVariant
		? getBaseResultsModuleOrder(renderVariant)
		: (Object.keys(RESULTS_MODULE_REGISTRY) as ResultModuleKey[]);

	return sourceModules.filter((moduleKey) =>
		RESULTS_MODULE_REGISTRY[moduleKey].allowedSurfaces.includes(surface),
	);
}

export function getShareArtifactModuleOrder(): ResultModuleKey[] {
	return SHARE_ARTIFACT_MODULE_ORDER.filter((moduleKey) =>
		RESULTS_MODULE_REGISTRY[moduleKey].allowedSurfaces.includes(
			"share_artifact",
		),
	);
}

export function applySharedLinkPrivacyToModuleOrder(
	moduleOrder: ResultModuleKey[],
	privacySettings: Partial<SharedLinkPrivacySettings> | null | undefined,
): ResultModuleKey[] {
	if (!privacySettings) return [...moduleOrder];

	const hiddenModules = new Set<ResultModuleKey>();
	for (const [privacyKey, modules] of Object.entries(
		SHARED_LINK_PRIVACY_MODULE_FILTERS,
	) as Array<[keyof SharedLinkPrivacySettings, ResultModuleKey[]]>) {
		if (privacySettings[privacyKey]) {
			for (const moduleKey of modules) hiddenModules.add(moduleKey);
		}
	}

	return moduleOrder.filter((moduleKey) => !hiddenModules.has(moduleKey));
}

export function isSduiEligibleResultsModule(key: ResultModuleKey): boolean {
	return RESULTS_MODULE_REGISTRY[key].sduiEligible;
}
